import { policyClient } from '@api/sdk-client';
import { IOutsetaToken } from '@models/outseta/outseta';
import { IFindOneMembershipPackagesRequest } from '@models/policy/request';
import {
  IFindCancellationReasonsResponse,
  IFindOneMembershipPackagesResponse,
  IFindOneOutsetaPlanResponse,
  IPasswordResetResponse,
} from '@models/policy/response';
import {
  membershipPackagesControllerFindUnique,
  outsetaControllerGetCancellationReasons,
  outsetaControllerGetPlan,
  outsetaControllerGetPlans,
  outsetaControllerResetPassword,
} from '@webidoo-eng/jooice-policy';
import { jwtDecode } from 'jwt-decode';

// The policy SDK bundles its own @hey-api/client-axios types which diverge
// from the top-level package. Casting options is required until aligned.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const opts = (o: Record<string, unknown>) => o as any;

const policyAPI = {
  getPolicyPlanInfo: async (
    params?: IFindOneMembershipPackagesRequest,
  ): Promise<IFindOneMembershipPackagesResponse> => {
    const token = localStorage.getItem('Outseta.nocode.accessToken');
    if (!token) throw new Error('policyApi.findOne: No token');
    const decode: IOutsetaToken = jwtDecode(token);
    const outsetaPlanUid: string = decode['outseta:planUid'];
    const addOnsUids: string[] = decode['outseta:addOnUids'];
    const subscriptionUid: string | undefined = decode['outseta:subscriptionUid'];

    const { data } = await membershipPackagesControllerFindUnique(
      opts({
        client: policyClient,
        path: { outsetaPlanUid },
        query: {
          addOnsUids,
          ...(subscriptionUid ? { subscriptionUid } : {}),
          ...params,
        },
      }),
    );
    return data as unknown as IFindOneMembershipPackagesResponse;
  },

  getOutsetaPlan: async (planId: string) => {
    const { data } = await outsetaControllerGetPlan(
      opts({
        client: policyClient,
        path: { outsetaPlanUid: planId },
      }),
    );
    return data as IFindOneOutsetaPlanResponse;
  },

  getOutsetaPlans: async () => {
    const { data } = await outsetaControllerGetPlans(opts({ client: policyClient }));
    return data as IFindOneOutsetaPlanResponse;
  },

  passwordReset: async (body: { email: string }) => {
    const { data } = await outsetaControllerResetPassword(opts({ client: policyClient, body }));
    return data as IPasswordResetResponse;
  },

  getCancellationReasons: async (): Promise<string[]> => {
    const { data } = await outsetaControllerGetCancellationReasons(opts({ client: policyClient }));
    const response = data as IFindCancellationReasonsResponse;
    const reasons = response?.data?.cancellationReasons;
    return Array.isArray(reasons) ? reasons.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [];
  },
};

export default policyAPI;
